import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { organizationService } from "../../../services/api/organization";
import { toast } from "sonner";
import { Shield, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import DPAContent from "../data-processing/DPAContent";
import { format } from "date-fns";

import { useOrganizationData } from "../../../hooks/useOrganizationData";

const DpaPrePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("Id") || "";
  const roleParam = searchParams.get("role") as "ceo" | "cto" | null;

  const { data: orgData, loading, error: fetchError } = useOrganizationData(id);
  const [approving, setApproving] = useState<"ceo" | "cto" | null>(null);
  const [approved, setApproved] = useState<"ceo" | "cto" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const today = format(new Date(), "do MMMM, yyyy");

  const handleApprove = async (role: "ceo" | "cto") => {
    if (!id) {
      toast.error("Organization ID is missing.");
      return;
    }
    setApproving(role);
    setError(null);
    try {
      await organizationService.approveDocument(id);
      setApproved(role);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || "Failed to approve agreement.";
      setError(msg);
      toast.error(msg);
    } finally {
      setApproving(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading document...</p>
        </div>
      </div>
    );
  }

  const rolesToShow: ("ceo" | "cto")[] = roleParam
    ? [roleParam]
    : ["ceo", "cto"];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 flex flex-col items-center">
      <div className="w-full max-w-[1024px] space-y-6">
        {/* Approval Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden sticky top-4 z-10">
          <div className="bg-gradient-to-r from-indigo-700 to-primary px-6 py-5 text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                <Shield size={22} className="text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">DPA Approval</h1>
                <p className="text-white/80 text-sm mt-0.5">
                  Review the Data Processing Addendum below and approve for{" "}
                  <span className="font-semibold text-white">
                    {orgData.name}
                  </span>
                </p>
              </div>
            </div>
            {id && (
              <div className="hidden sm:flex items-center gap-2 text-xs text-white/70">
                <span>Org ID:</span>
                <code className="bg-black/10 px-2 py-0.5 rounded text-white/90">
                  {id.substring(0, 8)}...
                </code>
              </div>
            )}
          </div>

          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            {(error || fetchError) && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <AlertCircle
                  size={16}
                  className="text-red-500 mt-0.5 shrink-0"
                />
                <p className="text-sm text-red-700">{error || fetchError}</p>
              </div>
            )}

            {approved ? (
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
                <CheckCircle size={22} className="text-green-600 shrink-0" />
                <div>
                  <p className="font-semibold text-green-800 text-sm">
                    DPA Approved by {approved.toUpperCase()}
                  </p>
                  <p className="text-xs text-green-600 mt-0.5">
                    Your approval has been recorded successfully.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <p className="text-sm text-gray-600 flex-1">
                  By clicking <strong>Approve</strong>, you confirm the DPA is
                  approved for execution
                </p>
                <div className="flex gap-2 w-full sm:w-auto">
                  {rolesToShow.map((role) => (
                    <button
                      key={role}
                      onClick={() => handleApprove(role)}
                      disabled={approving !== null}
                      className={`flex-1 sm:flex-none py-2 px-5 rounded-lg font-semibold text-sm uppercase tracking-wide transition-all duration-200 flex items-center justify-center gap-2 shadow-sm
                        ${
                          approving === role
                            ? "bg-primary/60 text-white cursor-not-allowed"
                            : "bg-primary text-white hover:bg-primary/90 hover:shadow-md active:scale-[0.98]"
                        }
                      `}
                    >
                      {approving === role ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Approving...
                        </>
                      ) : (
                        <>
                          <CheckCircle size={16} />
                          Approve ({role})
                        </>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Read-Only Document Content */}
        <div>
          <DPAContent
            clientName={orgData.name}
            representativeName={orgData.representativeName}
            representativeRole={orgData.representativeRole}
            date={today}
            effectiveDate={today}
            readOnly={true}
          />
        </div>
      </div>
    </div>
  );
};

export default DpaPrePage;
